import React from 'react';
import dayjs from 'dayjs';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';

import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';

import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';

import { Notifier } from '../../system/services/notificationManager';
import DataTable from '../shared/data-table';
import {
  MobileListCard,
  MobileListDataTableProps,
} from '../shared/mobile-list-card';

import {
  IngestionsQuery,
  useRemoveIngestionMutation,
} from '../../../types/generated-types';

export interface IngestionsTableProps {
  ingestions: IngestionsQuery['ingestions'];
  loading: boolean;
}

interface RemoveIngestionDialogProps {
  open: boolean;
  handleClose: () => void;
  removeIngestion: () => void;
}

const RemoveIngestionDialog = (props: RemoveIngestionDialogProps) => {
  const { open, handleClose, removeIngestion } = props;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Typography variant="h4" sx={{ padding: '15px' }}>
        Remove Ingestion
      </Typography>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure? this action cannot be reversed
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ color: '#fff' }}
          onClick={() => {
            removeIngestion();
            handleClose();
          }}
          autoFocus
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Table that lists all in-progress or completed ingestions
export function IngestionsTable(props: IngestionsTableProps) {
  const { ingestions, loading } = props;
  const isSmall = useIsSmallScreen();

  /* State */
  // Sort records from most to least recent by default
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'date', sort: 'desc' },
  ]);
  const [ingestionId, setIngestionId] = React.useState<string>('');
  const [openDialog, setRemoveIngestionDialog] = React.useState(false);

  /* Queries, Mutations, Subscriptions */
  const [removeIngestion] = useRemoveIngestionMutation({
    onCompleted() {
      Notifier.success('Removed ingestion record');
    },
    onError() {
      Notifier.error('Error removing ingestion record');
    },
  });

  const handleRemoveIngestionClose = () => {
    setRemoveIngestionDialog(false);
  };

  const handleRemoveIngestion = async () => {
    await removeIngestion({ variables: { id: ingestionId } });
    setIngestionId('');
  };

  // Map ingestion records to rows
  const rows = ingestions.map((ingestion) => ({
    ...ingestion,
    id: ingestion._id,
    ingestedBy: ingestion.user?.name || 'Unknown user',
    removeIngestion: () => {
      setIngestionId(ingestion._id);
      setRemoveIngestionDialog(true);
    },
  }));

  const dateColumn = isSmall
    ? ({
        field: 'date',
        headerName: 'Date Ingested',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams<Partial<any>>) => {
          return (
            <MobileListCard>
              <Typography
                component="div"
                sx={{ fontSize: 15, fontWeight: 800 }}
                color="text.secondary"
              >
                {dayjs(params.value).format('MM/DD/YYYY h:mma')}
              </Typography>
              <Typography
                variant="h5"
                component="div"
                color="text.secondary"
                sx={{ fontSize: 12, whiteSpace: 'normal' }}
              >
                <b>File Name:</b> {params.row.fileName}
              </Typography>
              <Typography sx={{ fontSize: 12 }} color="text.secondary">
                <b>Ingested By:</b> {params.row.ingestedBy}
              </Typography>
              <Typography sx={{ fontSize: 12 }} color="text.secondary">
                <b>Status:</b> {params.row.status}
              </Typography>
            </MobileListCard>
          );
        },
      } as GridColDef)
    : {
        field: 'date',
        headerName: 'Date ingested',
        width: 180,
        valueFormatter: (params: any) =>
          dayjs(params.value).format('MM/DD/YYYY h:mma'),
      };

  const columns: GridColDef[] = [
    { ...dateColumn },
    { field: 'fileName', headerName: 'File name', flex: 1 },
    { field: 'ingestedBy', headerName: 'Ingested by', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: isSmall ? 50 : 100,
      renderCell: (params) => (
        <Box
          sx={{
            width: 'auto',
            height: isSmall ? '90%' : '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            backgroundColor: isSmall ? 'lightgray' : 'transparent',
          }}
        >
          <IconButton
            sx={{ color: '#007F76' }}
            // title="Remove ingestion record. The property will not be deleted."
            onClick={params.row?.removeIngestion}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
        cursor: 'pointer',
        maxWidth: isSmall ? 'unset' : 'calc(100vw - 67px)',
      }}
    >
      <DataTable
        {...(isSmall ? MobileListDataTableProps : {})}
        columns={columns}
        rows={rows}
        rowHeight={isSmall ? 100 : undefined}
        pageSize={isSmall ? 4 : undefined}
        suppressAutoPageSize={isSmall}
        columnVisibilityModel={{
          action: !isSmall,
          fileName: !isSmall,
          ingestedBy: !isSmall,
          status: !isSmall,
        }}
        sortModel={sortModel}
        onSortModelChange={(model: any) => {
          //TODO: Peter: INCLUDE TYPE
          setSortModel(model);
        }}
        loading={loading}
        sxProps={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'default',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
      <RemoveIngestionDialog
        open={openDialog}
        handleClose={() => handleRemoveIngestionClose()}
        removeIngestion={() => handleRemoveIngestion()}
      />
    </div>
  );
}
